/**
 * Numbers, over time and side by side.
 *
 * The application draws these with a four colour score scale, letter grades
 * from A to F, a six colour pie, and gradient-filled areas. Here a
 * measurement is ink, series are told apart by the label at the end of the
 * line rather than by colour, and the only colour that appears is the one
 * that means somebody has to do something.
 */
import { useId, useState, type ReactNode } from 'react'
import { Check, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { Fig, Input } from './primitives'
import './data.css'

/* ---------------- Shared plotting maths ---------------- */

export interface Point {
  label: string
  value: number
}

const path = (values: number[], w: number, h: number, pad: number, max: number) => {
  const step = values.length > 1 ? (w - pad * 2) / (values.length - 1) : 0
  return values
    .map((v, i) => `${i === 0 ? 'M' : 'L'} ${(pad + i * step).toFixed(1)} ${(h - pad - (v / max) * (h - pad * 2)).toFixed(1)}`)
    .join(' ')
}

/* ---------------- A line over time ---------------- */

/**
 * One line in ink. A second series is drawn quieter and both are named at the
 * end of their own line, so nothing depends on telling two colours apart.
 *
 * Pointing at the line reads out that one point. A trend where only the last
 * value can be read is a picture of a shape; a person looking at thirty days
 * of traffic wants to know what Tuesday was.
 */
export function Trend({
  points,
  compare,
  height = 190,
  unit,
}: {
  points: Point[]
  compare?: { label: string; values: number[] }
  height?: number
  unit?: string
}) {
  const w = 640
  const pad = 26
  const values = points.map((p) => p.value)
  const max = Math.max(...values, ...(compare?.values ?? []), 1) * 1.15
  const gridAt = [0, 0.5, 1]
  const last = values[values.length - 1]
  const lastY = height - pad - (last / max) * (height - pad * 2)
  const clip = useId()

  const [at, setAt] = useState<number | null>(null)
  const step = points.length > 1 ? (w - pad * 2) / (points.length - 1) : 0
  const xOf = (i: number) => pad + i * step
  const yOf = (v: number) => height - pad - (v / max) * (height - pad * 2)
  const here = at !== null ? points[at] : null

  return (
    <svg
      className="ds-plot"
      width={w}
      height={height}
      viewBox={`0 0 ${w} ${height}`}
      role="img"
      aria-label={`${points[0].label} to ${points[points.length - 1].label}`}
      onMouseLeave={() => setAt(null)}
    >
      {gridAt.map((g) => (
        <line key={g} className="ds-plot__grid" x1={pad} x2={w - pad} y1={pad + g * (height - pad * 2)} y2={pad + g * (height - pad * 2)} />
      ))}
      <clipPath id={clip}>
        <rect x={pad} y={0} width={w - pad * 2} height={height} />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        <path className="ds-plot__area" d={`${path(values, w, height, pad, max)} L ${w - pad} ${height - pad} L ${pad} ${height - pad} Z`} />
        {compare && <path className="ds-plot__line ds-plot__line--quiet" d={path(compare.values, w, height, pad, max)} />}
        <path className="ds-plot__line" d={path(values, w, height, pad, max)} />
      </g>
      <circle className="ds-plot__dot" cx={w - pad} cy={lastY} r={4} />
      <text className="ds-plot__tag" x={w - pad - 6} y={lastY - 12} textAnchor="end">
        {last}
        {unit ? ` ${unit}` : ''}
      </text>
      {points.map((p, i) =>
        i === 0 || i === points.length - 1 || i === Math.floor(points.length / 2) ? (
          <text key={p.label} className="ds-plot__axis" x={pad + (i * (w - pad * 2)) / (points.length - 1)} y={height - 6} textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}>
            {p.label}
          </text>
        ) : null,
      )}

      {/* The one being pointed at */}
      {here && at !== null && (
        <g className="ds-plot__at" aria-hidden="true">
          <line className="ds-plot__atline" x1={xOf(at)} x2={xOf(at)} y1={pad} y2={height - pad} />
          <circle className="ds-plot__dot" cx={xOf(at)} cy={yOf(here.value)} r={4} />
          <text
            className="ds-plot__attag"
            x={Math.min(Math.max(xOf(at), pad + 40), w - pad - 40)}
            y={14}
            textAnchor="middle"
          >
            {here.label}: {here.value.toLocaleString()}
            {unit ? ` ${unit}` : ''}
          </text>
        </g>
      )}

      {/* One band per point, so the whole width of the plot is pointable. */}
      {points.map((p, i) => (
        <rect
          key={`${p.label}-band`}
          className="ds-plot__band"
          x={xOf(i) - step / 2}
          y={0}
          width={Math.max(step, 1)}
          height={height}
          onMouseEnter={() => setAt(i)}
        />
      ))}
    </svg>
  )
}

/* ---------------- Bars over time ---------------- */

export function Columns({ points, height = 150, lookBelow }: { points: Point[]; height?: number; lookBelow?: number }) {
  const w = 640
  const pad = 22
  const max = Math.max(...points.map((p) => p.value), 1)
  const slot = (w - pad * 2) / points.length
  const barW = Math.min(slot * 0.56, 26)
  return (
    <svg className="ds-plot" width={w} height={height} viewBox={`0 0 ${w} ${height}`} role="img" aria-label="Each day">
      <line className="ds-plot__grid" x1={pad} x2={w - pad} y1={height - pad} y2={height - pad} />
      {points.map((p, i) => {
        const barH = Math.max((p.value / max) * (height - pad * 2), p.value > 0 ? 2 : 0)
        const quiet = lookBelow !== undefined && p.value < lookBelow
        return (
          <rect
            key={p.label}
            className={cn('ds-plot__bar', quiet && 'ds-plot__bar--quiet')}
            x={pad + i * slot + (slot - barW) / 2}
            y={height - pad - barH}
            width={barW}
            height={barH}
            rx={3}
          />
        )
      })}
      {points.map((p, i) =>
        i % Math.ceil(points.length / 6) === 0 ? (
          <text key={p.label} className="ds-plot__axis" x={pad + i * slot + slot / 2} y={height - 6} textAnchor="middle">
            {p.label}
          </text>
        ) : null,
      )}
    </svg>
  )
}

/* ---------------- A line inside a sentence ---------------- */

/**
 * A line small enough to sit in a sentence. It is drawn between its own
 * lowest and highest value rather than from zero, because at this size a line
 * measured from zero is a flat line and says nothing.
 */
export function Spark({ values, label }: { values: number[]; label: string }) {
  const w = 84
  const h = 22
  const lo = Math.min(...values)
  const hi = Math.max(...values)
  const span = hi - lo || 1
  const shifted = values.map((v) => (v - lo) / span + 0.12)
  return (
    <span className="ds-spark">
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label}>
        <path className="ds-plot__line" style={{ strokeWidth: 1.5 }} d={path(shifted, w, h, 3, 1.24)} />
      </svg>
    </span>
  )
}

/* ---------------- How a total splits up ---------------- */

/**
 * One bar and a list, instead of a pie. Six wedges in six colours is six
 * things nobody can compare; a list can be read straight down and says the
 * number as well as the share.
 */
export function Share({ items, of }: { items: Point[]; of?: number }) {
  const total = of ?? items.reduce((n, i) => n + i.value, 0)
  const pct = (v: number) => (total > 0 ? Math.round((v / total) * 100) : 0)
  return (
    <div>
      <div className="ds-share__bar" role="img" aria-label={items.map((i) => `${i.label} ${pct(i.value)} percent`).join(', ')}>
        {items.map((i) => (
          <span key={i.label} className="ds-share__seg" style={{ width: `${pct(i.value)}%` }} />
        ))}
      </div>
      <ul className="ds-share__list">
        {items.map((i) => (
          <li key={i.label} className="ds-share__row">
            <span className="ds-share__key">
              <span className="ds-share__chip" aria-hidden="true" />
              {i.label}
            </span>
            <span className="ds-share__num">
              <Fig>{i.value}</Fig>
            </span>
            <span className="ds-share__pct">{pct(i.value)}%</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ---------------- Not enough to draw yet ---------------- */

export function NotYet({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="ds-plotempty">
      <div>
        <b>{title}</b>
        <span>{children}</span>
      </div>
    </div>
  )
}

/* ---------------- What happened, in order ---------------- */

export interface WhenItem {
  title: string
  detail?: string
  stamp?: string
  state?: 'done' | 'now' | 'waiting'
}

export function When({ items }: { items: WhenItem[] }) {
  return (
    <ol className="ds-when">
      {items.map((i) => (
        <li key={i.title} className="ds-when__item" data-state={i.state ?? 'waiting'}>
          <span className="ds-when__mark" aria-hidden="true">
            {i.state === 'done' ? <Check /> : null}
          </span>
          <div className="ds-when__body">
            <div className="ds-when__title">{i.title}</div>
            {i.detail && <p className="ds-when__detail">{i.detail}</p>}
            {i.stamp && <div className="ds-when__stamp">{i.stamp}</div>}
          </div>
        </li>
      ))}
    </ol>
  )
}

/* ---------------- Who came, and what they read ---------------- */

export function Log({ rows }: { rows: { who: string; what: string; when: string }[] }) {
  return (
    <ul className="ds-log">
      {rows.map((r, i) => (
        <li key={`${r.who}-${i}`} className="ds-log__row">
          <span className="ds-log__who">{r.who}</span>
          <span className="ds-log__what">{r.what}</span>
          <span className="ds-log__when">{r.when}</span>
        </li>
      ))}
    </ul>
  )
}

/* ---------------- Moving through a long list ---------------- */

export function Pager({
  page,
  pages,
  showing,
  onChange,
}: {
  page: number
  pages: number
  showing?: string
  onChange?: (p: number) => void
}) {
  const nums: (number | 'gap')[] = []
  for (let n = 1; n <= pages; n += 1) {
    if (n === 1 || n === pages || Math.abs(n - page) <= 1) nums.push(n)
    else if (nums[nums.length - 1] !== 'gap') nums.push('gap')
  }
  return (
    <div className="ds-pager">
      {showing && <div className="ds-pager__count">{showing}</div>}
      <div className="ds-pager__controls">
        <button type="button" className="ds-pager__num" disabled={page === 1} onClick={() => onChange?.(page - 1)} aria-label="Previous">
          <ChevronLeft aria-hidden="true" />
        </button>
        {nums.map((n, i) =>
          n === 'gap' ? (
            <span key={`gap-${i}`} className="ds-pager__gap">
              ...
            </span>
          ) : (
            <button key={n} type="button" className="ds-pager__num" aria-current={n === page || undefined} onClick={() => onChange?.(n)}>
              {n}
            </button>
          ),
        )}
        <button type="button" className="ds-pager__num" disabled={page === pages} onClick={() => onChange?.(page + 1)} aria-label="Next">
          <ChevronRight aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

/* ---------------- Narrowing a list down ---------------- */

export function Filters({ children }: { children: ReactNode }) {
  return <div className="ds-filters">{children}</div>
}

export function FilterSearch({ placeholder = 'Search' }: { placeholder?: string }) {
  return (
    <div className="ds-filters__search">
      <Input placeholder={placeholder} aria-label={placeholder} />
    </div>
  )
}

export function Chip({
  children,
  count,
  on,
  onClick,
  clearable,
}: {
  children: ReactNode
  count?: number
  on?: boolean
  onClick?: () => void
  clearable?: boolean
}) {
  return (
    <button type="button" className="ds-chip" aria-pressed={on || undefined} onClick={onClick}>
      {children}
      {count !== undefined && <span className="ds-chip__count">{count}</span>}
      {clearable && on && <X aria-hidden="true" />}
    </button>
  )
}

/* ---------------- Something worth telling them ---------------- */

/** What we noticed, why it matters to them, and the one thing to do. */
export function Insight({ title, why, children }: { title: string; why: string; children?: ReactNode }) {
  return (
    <div className="ds-insight">
      <div className="ds-insight__title">{title}</div>
      <p className="ds-insight__why">{why}</p>
      {children && <div className="ds-insight__foot">{children}</div>}
    </div>
  )
}

/* ---------------- What to do first ---------------- */

export function ToDo({
  items,
}: {
  items: { title: string; detail: string; cost: string; action?: ReactNode }[]
}) {
  return (
    <ol className="ds-todo">
      {items.map((it, i) => (
        <li key={it.title} className="ds-todo__item">
          <span className="ds-todo__rank" aria-hidden="true">
            {i + 1}
          </span>
          <div className="ds-todo__body">
            <div className="ds-todo__title">{it.title}</div>
            <p className="ds-todo__detail">{it.detail}</p>
            <div className="ds-todo__cost">{it.cost}</div>
          </div>
          {it.action && <div className="ds-todo__act">{it.action}</div>}
        </li>
      ))}
    </ol>
  )
}

export { Search as SearchIcon }
